import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ExportOutlined } from '@ant-design/icons';
import { Col, Divider, Row, Spin, Table, Typography, message } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { GetTeamsBySalesforceAccountIdResponse } from '@cbinsights/userv2service/userv2service';
import moment from 'moment';
import useHasAccess from 'client/utils/permissions-validation/hooks/useHasAccess';
import { PermissionType } from '@cbinsights/adminpermissionservice/adminpermissionservice';
import useShowPagination from '../../hooks/useShowPagination';
import { useGetTeamsBySalesforceAccountId } from '../../services/hooks/useGetTeamsBySalesforceAccountId';
import { useGetSalesforceAccountInfoQuery } from '../../services/hooks/useGetSalesforceAccountInfo';
import { teamPermissions } from '../../permissions/Team';
import { CreateNewTeam } from '../CreateNewTeam';

const SalesforceAccountPage = () => {
  const { id } = useParams<{ id: string }>();

  const hasAccessToTeamPage = useHasAccess({
    pageId: teamPermissions.pageId,
    permissionType: PermissionType.PUBLISH,
  });

  const {
    data,
    error,
    isLoading,
    refetch: refetchTeams,
  } = useGetTeamsBySalesforceAccountId({
    enabled: !!id,
    params: { salesforce_account_id: id },
  });

  const { data: salesforceAccountInfo, isLoading: isLoadingAccountInfo } =
    useGetSalesforceAccountInfoQuery({
      enabled: !!id,
      params: { accountId: id },
    });

  const showPagination = useShowPagination({
    data: data?.teams,
    config: { pageSize: 25 },
  });

  const onSuccessCreateNewTeam = (idTeam: number) => {
    message.success(`Team ${idTeam} created successfully.`);
    refetchTeams();
  };

  const teamList = useMemo(() => {
    if (!data?.teams) return [];

    return data.teams.map((team) => ({
      ...team,
      package_start: team.package_start
        ? moment(team.package_start).format('MM-DD-YYYY')
        : '',
      package_end: team.package_end
        ? moment(team.package_end).format('MM-DD-YYYY')
        : '',
    }));
  }, [data]);

  if (!hasAccessToTeamPage) {
    return (
      <Row justify="center" className="mt-8">
        <Col xs={{ span: 22 }} md={{ span: 18 }} lg={{ span: 16 }}>
          <div>You do not have access to view this page.</div>
        </Col>
      </Row>
    );
  }

  if (!id) {
    return (
      <Row justify="center" className="mt-8">
        <Col xs={{ span: 22 }} md={{ span: 18 }} lg={{ span: 16 }}>
          <div>No Salesforce Account ID provided.</div>
        </Col>
      </Row>
    );
  }

  if (isLoading || isLoadingAccountInfo) {
    return (
      <Row justify="center" className="mt-8">
        <Col xs={{ span: 22 }} md={{ span: 18 }} lg={{ span: 16 }}>
          <div className="text-center">
            <Spin size="large" />
          </div>
        </Col>
      </Row>
    );
  }

  if (error) {
    return (
      <Row justify="center" className="mt-8">
        <Col xs={{ span: 22 }} md={{ span: 18 }} lg={{ span: 16 }}>
          <div>Error loading teams: {error.message}</div>
        </Col>
      </Row>
    );
  }

  const columns: ColumnsType<
    GetTeamsBySalesforceAccountIdResponse['teams'][0]
  > = [
    {
      title: 'Team ID',
      dataIndex: 'id_team',
      key: 'idTeam',
      render: (idTeam) => (
        <span>
          <Link className="underline text-blue-500" to={`/teams/${idTeam}`}>
            {idTeam}
          </Link>
        </span>
      ),
    },
    { title: 'Team Name', dataIndex: 'name', key: 'teamName' },
    { title: 'Package', dataIndex: 'id_package', key: 'package' },
    { title: 'Start Date', dataIndex: 'package_start', key: 'startDate' },
    { title: 'End Date', dataIndex: 'package_end', key: 'endDate' },
  ];

  return (
    <Row justify="center" className="mt-8">
      <Col xs={{ span: 22 }} md={{ span: 18 }} lg={{ span: 16 }}>
        <Typography.Title level={3}>
          Salesforce Account Id: {id}
        </Typography.Title>
      </Col>

      <CreateNewTeam sfAccountId={id} onSuccess={onSuccessCreateNewTeam} />

      {salesforceAccountInfo && (
        <Col xs={{ span: 22 }} md={{ span: 18 }} lg={{ span: 16 }}>
          <Typography.Title level={4} className="!font-medium mt-4 pl-2">
            Account Information
          </Typography.Title>
          <Divider className="border-t-2 border-black border-solid" />

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Row justify="space-between" className="mb-2">
                <Col span={12}>
                  <Typography.Title level={5} className="!font-medium pl-2">
                    Account Name:
                  </Typography.Title>
                </Col>
                <Col span={12} className="text-right">
                  {salesforceAccountInfo.accountName || 'N/A'}
                </Col>
              </Row>

              <Row justify="space-between" className="mb-2">
                <Col span={12}>
                  <Typography.Title level={5} className="!font-medium pl-2">
                    Account ID:
                  </Typography.Title>
                </Col>
                <Col span={12} className="text-right">
                  {salesforceAccountInfo.accountId && (
                    <a
                      href={`https://cbinsights.lightning.force.com/lightning/r/Account/${salesforceAccountInfo.accountId}/view`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-500"
                    >
                      {salesforceAccountInfo.accountId} <ExportOutlined />
                    </a>
                  )}
                </Col>
              </Row>

              <Row justify="space-between" className="mb-2">
                <Col span={12}>
                  <Typography.Title level={5} className="!font-medium pl-2">
                    Account Owner:
                  </Typography.Title>
                </Col>
                <Col span={12} className="text-right">
                  {salesforceAccountInfo.accountOwner || 'N/A'}
                </Col>
              </Row>
            </Col>
            <Col xs={24} lg={12}>
              <Row justify="space-between" className="mb-2">
                <Col span={12}>
                  <Typography.Title level={5} className="!font-medium pl-2">
                    Account Owner Email:
                  </Typography.Title>
                </Col>
                <Col span={12} className="text-right">
                  {salesforceAccountInfo.accountOwnerEmail || 'N/A'}
                </Col>
              </Row>

              <Row justify="space-between" className="mb-2">
                <Col span={12}>
                  <Typography.Title level={5} className="!font-medium pl-2">
                    Renewal Date:
                  </Typography.Title>
                </Col>
                <Col span={12} className="text-right">
                  {salesforceAccountInfo.renewalDate
                    ? moment(salesforceAccountInfo.renewalDate).format(
                        'MM-DD-YYYY'
                      )
                    : 'N/A'}
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      )}

      <Col
        xs={{ span: 22 }}
        md={{ span: 18 }}
        lg={{ span: 16 }}
        className="mt-4"
      >
        <Typography.Title level={4}>Teams</Typography.Title>
        <Table
          bordered
          columns={columns}
          dataSource={teamList}
          pagination={showPagination}
          loading={isLoading}
          rowKey="id_team"
        />
      </Col>
    </Row>
  );
};

export default SalesforceAccountPage;
